'use client';

import { useState } from 'react';

import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect';

/**
 * Hook that tracks the visibility state of the current document/tab using the Page Visibility API.
 * Useful for pausing animations, stopping timers, or optimizing performance when the tab is not visible.
 * Handles server-side rendering by defaulting to visible state.
 *
 * @returns {boolean} True if the document is currently visible, false if hidden/minimized.
 */
export const useVisibilityChange = () => {
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    if (typeof document !== 'undefined') {
      return document.visibilityState === 'visible';
    }
    return true; // Assume visible on server-side
  });

  useIsomorphicLayoutEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};
